/**
 * 開発者アカウントの復旧まわり（0013 identity）: パスワードを忘れた / 再設定 / メール確認、と未確認の帯。
 *
 *   /forgot-password           メールアドレスを入れて再設定リンクを送る（アカウントの有無にかかわらず同じ表示 = 列挙させない）
 *   /reset-password#token=…    メールのリンク先。新しいパスワード → 全セッション失効 → 完了の画面（外した物・残った鍵があれば・pages/ResetDone.tsx）→ ログイン画面へ
 *                              開いたらアカウント名（メール）を聞いて欄に出し、ログイン画面へ渡す（2026-10-09 core/reset-username・../resetEmail.ts）
 *   /verify-email#token=…      メールのリンク先。pages/VerifyEmail.tsx に分けた（2026-09-28: セッションの有無で確認 / パスワードの決め直しに分かれる）
 *
 * トークンは URL の fragment で受け取る（ダッシュボードの配信サーバー・CDN のログに残さない。サーバー側の説明は
 * apps/server/src/modules/platform/recovery.ts）。読んだらすぐ URL から消す（画面共有・ブラウザ履歴に残さない）。
 */
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, type NavigateFunction } from 'react-router-dom';
import { api, API_BASE, ApiError } from '../api';
import { LanguageSwitch, Logo } from '../components/ui';
import { PRODUCT_NAME } from '../productName';
import { errorText, useT } from '../i18n';
import { isLaunchHidden } from '../launchHidden';
import { useSession } from '../session';
import { ResendVerificationButton } from '../components/EmailUnverified';
// パスワードの長さの規則とフォームの属性は authForms.ts の 1 か所（2026-09-27 creator/auth-forms: 自動入力・パスワードの生成が効く形に）
import { authFormProps, emailFieldProps, MIN_PASSWORD, passwordFieldProps, passwordLengthOk } from '../authForms';
import { hasResetFindings, readResetResult, RESET_DONE_PATH, setResetResult } from '../secretKeys';
import { readResetAccount, rememberResetEmail, tabStorage, usernameFieldProps } from '../resetEmail';

/** fragment の token を 1 回だけ読み、URL から消す（pages/VerifyEmail.tsx も使う） */
export function useFragmentToken(): string | null {
  const [token] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get('token'));
  useEffect(() => {
    if (window.location.hash) window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
  }, []);
  return token;
}

/** ログイン画面と同じ枠の 1 カラム版（左の図版は無し。復旧の画面は短い用事なので）。pages/VerifyEmail.tsx も使う */
// title は ReactNode（確認の画面の見出しに、外から来た値を <code> で差し込むため・2026-10-03 console/plain-confirm）
export function AuthShell({ title, lead, children }: { title: ReactNode; lead?: ReactNode; children: ReactNode }) {
  return (
    <main className="auth auth-single">
      <section className="auth-panel">
        <div className="auth-form">
          <div className="auth-form-top">
            <div className="auth-mobile-brand">
              <Logo size={28} />
              <span>{PRODUCT_NAME}</span>
            </div>
            <LanguageSwitch className="auth-lang" />
          </div>
          <h2>{title}</h2>
          {lead && <p className="muted">{lead}</p>}
          {children}
        </div>
      </section>
    </main>
  );
}

/**
 * ローカルのサーバー（KUMO_MAIL_PROVIDER=log）はメールを送らず outbox に貯める。開けるならその場所を案内する
 * （本番サーバーでは 404 = 何も出さない）
 */
function DevOutboxHint() {
  const { tr } = useT();
  const [available, setAvailable] = useState(false);
  useEffect(() => {
    let alive = true;
    api('/v1/dev/outbox', { auth: false })
      .then(() => alive && setAvailable(true))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);
  if (!available) return null;
  const url = `${API_BASE}/v1/dev/outbox`;
  return (
    <p className="alert alert-info small">
      {tr('recover.devOutbox', {
        link: (
          <a href={url} target="_blank" rel="noreferrer">
            <code>/v1/dev/outbox</code>
          </a>
        )
      })}
    </p>
  );
}

export function ForgotPasswordPage() {
  const { t } = useT();
  const [email, setEmail] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api('/v1/developers/password/forgot', { method: 'POST', body: { email: email.trim() }, auth: false });
      setSentTo(email.trim());
    } catch (err) {
      setError(errorText(err as ApiError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title={t('recover.forgotTitle')} lead={sentTo ? undefined : t('recover.forgotLead')}>
      {sentTo ? (
        <>
          <p className="alert alert-ok" role="status">
            {t('recover.sent', { email: sentTo })}
          </p>
          <DevOutboxHint />
        </>
      ) : (
        <form className="auth-form" {...authFormProps('forgot', '/forgot-password')} onSubmit={(e) => void submit(e)}>
          <label className="field" htmlFor="email">
            <span>{t('auth.email')}</span>
            <input {...emailFieldProps()} value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
          </label>
          {error && (
            <p className="alert alert-error" role="alert">
              {error}
            </p>
          )}
          <button type="submit" className="btn btn-primary btn-block" disabled={busy || !email.trim()}>
            {busy ? t('auth.busy') : t('recover.send')}
          </button>
        </form>
      )}
      <p className="auth-switch">
        <Link to="/login">{t('recover.backToLogin')}</Link>
      </p>
    </AuthShell>
  );
}

/**
 * 再設定のリンクがどのアカウントの物か（2026-10-09 core/reset-username・WHY は ../resetEmail.ts の冒頭）。ResetPasswordPage と
 * pages/VerifyEmail.tsx の ChoosePassword が使う。token は本文で送る（URL に載せない）・サーバーは使用済みにしない。
 * 取れない（期限切れ・使用済み・通信の失敗）ときは null のまま = アカウント名の欄を出さない今までの画面（送信すれば今までどおりの失敗の案内）
 */
export function useResetAccount(token: string | null): string | null {
  const [email, setEmail] = useState<string | null>(null);
  useEffect(() => {
    if (!token) return;
    let alive = true;
    api<unknown>('/v1/developers/password/reset/account', { method: 'POST', body: { token }, auth: false })
      .then((res) => alive && setEmail(readResetAccount(res)))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [token]);
  return email;
}

/** 再設定のフォームの先頭に置くアカウント名の欄（読み取り専用）。email が無ければ何も出さない */
export function ResetAccountField({ email }: { email: string | null }) {
  const { t } = useT();
  if (!email) return null;
  return (
    <label className="field" htmlFor="username">
      <span>{t('recover.account')}</span>
      <input {...usernameFieldProps(email)} />
    </label>
  );
}

/**
 * 再設定の成功の後の行き先（ResetPasswordPage と pages/VerifyEmail.tsx の ChoosePassword の 1 か所）。
 * 見せる物（外したログイン方法・残っているサーバーの鍵）があれば完了の画面へ。結果はメモリだけで渡す（secretKeys.ts の冒頭の WHY）
 */
// email: 分かっていれば同じタブのストレージに置き、ログイン画面のメールの欄に入れる（URL には載せない・../resetEmail.ts）。
//   完了の画面を挟んでも、ログイン画面が読むまで残る
export function navigateAfterReset(navigate: NavigateFunction, res: unknown, email: string | null = null) {
  if (email) rememberResetEmail(tabStorage(), email);
  const result = readResetResult(res);
  if (hasResetFindings(result)) {
    setResetResult(result);
    navigate(RESET_DONE_PATH);
  } else {
    navigate('/login', { state: { notice: 'resetDone' } });
  }
}

export function ResetPasswordPage() {
  const { t } = useT();
  const token = useFragmentToken();
  const account = useResetAccount(token);
  const { developer, signOut } = useSession();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!passwordLengthOk(password)) return setError(t('auth.passwordTooShort', { min: MIN_PASSWORD }));
    if (password !== confirm) return setError(t('recover.mismatch'));
    setBusy(true);
    try {
      const res = await api<unknown>('/v1/developers/password/reset', { method: 'POST', body: { token, password }, auth: false });
      // サーバーは全セッションを失効させた。この端末のログイン状態も捨てる（次の操作で 401 になる前に）
      if (developer) await signOut();
      // 成功したら別の画面へ遷移する（2026-09-27 creator/auth-forms）。
      // WHY: 以前は同じ画面のまま完了の表示に書き換えていた。パスワード管理は「送信 → 遷移」で成功と判断するので、
      //   書き換えだと新しいパスワードの保存（更新）を出さないことがある。
      // 2026-09-28 creator/dashboard-pass（SEC-PKLOGIN-01）: 外したログイン方法・残っているサーバーの鍵があれば、ログインの前に
      //   完了の画面（pages/ResetDone.tsx）で見せる。無ければ今までどおりログイン画面で「変更しました」
      navigateAfterReset(navigate, res, account);
    } catch (err) {
      setError(errorText(err as ApiError));
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <AuthShell title={t('recover.resetTitle')}>
        <p className="alert alert-error" role="alert">
          {t('recover.missingToken')}
        </p>
        <p className="auth-switch">
          <Link to="/forgot-password">{t('recover.requestNew')}</Link>
        </p>
      </AuthShell>
    );
  }
  return (
    <AuthShell title={t('recover.resetTitle')} lead={t('recover.resetLead')}>
      {/* アカウント名（2026-10-09 core/reset-username）: 以前は「メールアドレスを URL に載せない・応答にも無い」ので欄を置けず、
          パスワード管理が新しいパスワードをどのアカウントの物か結び付けられなかった（/login で自動入力されない）。
          今はリンクの token でサーバーに聞き（useResetAccount・token は本文で送り使用済みにしない）、読み取り専用の username の欄に入れる。
          メールは URL に載せないまま。取れなければ欄を出さない今までの画面 */}
      {account && <p className="muted">{t('recover.resetFor', { email: account })}</p>}
      <form className="auth-form" {...authFormProps('reset', '/reset-password')} onSubmit={(e) => void submit(e)}>
        <ResetAccountField email={account} />
        <label className="field" htmlFor="new-password">
          <span>{t('recover.newPassword')}</span>
          <input {...passwordFieldProps('new')} value={password} onChange={(e) => setPassword(e.target.value)} required autoFocus aria-describedby="pw-hint" />
          <small id="pw-hint" className="muted">
            {t('auth.passwordHint', { min: MIN_PASSWORD })}
          </small>
        </label>
        <label className="field" htmlFor="confirm-password">
          <span>{t('recover.confirmPassword')}</span>
          <input {...passwordFieldProps('new', { confirm: true })} value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
        </label>
        {error && (
          <p className="alert alert-error" role="alert">
            {error}
          </p>
        )}
        <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
          {busy ? t('auth.busy') : t('recover.save')}
        </button>
      </form>
    </AuthShell>
  );
}

/**
 * 未確認の帯（トップバーの下）。ログインもプロジェクトの作成・デプロイも確認なしでできる（メールが届かないだけで開発を止めない）が、
 * 2026-09-28 Core SEC-DEVLOGIN-01 以降、sk_・MCP の接続・ログイン方法の追加・出金の設定は確認が済むまで使えない。それを先に知らせる
 */
export function VerifyEmailBanner() {
  const { developer } = useSession();
  const { t } = useT();
  if (!developer || developer.emailVerified !== false) return null;
  // 再送のボタンは操作の横の案内（components/EmailUnverified.tsx の EmailUnverifiedNotice）と同じ部品（2026-09-28 email-unverified）
  return (
    <div className="verify-banner" role="status">
      <span>{t(isLaunchHidden('payouts') ? 'verifyBanner.textLaunch' : 'verifyBanner.text', { email: developer.email })}</span>
      <ResendVerificationButton />
    </div>
  );
}
